import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getNearbySpots } from "@/services/touristSpotService";
import SafeImage from "@/components/base/SafeImage";
import { getSpotFallbackImage } from "@/utils/fallbackImage";
import type { TouristSpot } from "@/services/touristSpotService";

interface SpotNearbySpotsProps {
  districtSlug: string;
  districtName: string;
  excludeSlug: string;
}

export default function SpotNearbySpots({
  districtSlug,
  districtName,
  excludeSlug,
}: SpotNearbySpotsProps) {
  const [spots, setSpots] = useState<TouristSpot[]>([]);

  useEffect(() => {
    let active = true;
    getNearbySpots(districtSlug, excludeSlug, 4)
      .then((list) => {
        if (active) setSpots(list);
      })
      .catch(() => {
        if (active) setSpots([]);
      });
    return () => {
      active = false;
    };
  }, [districtSlug, excludeSlug]);

  if (spots.length === 0) return null;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wider text-foreground-900">
          <span className="flex h-4 w-4 items-center justify-center text-accent-600">
            <i className="ri-compass-3-line text-base" />
          </span>
          More spots nearby
        </h3>
        <span className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-bold text-foreground-600">
          {spots.length}
        </span>
      </div>

      <ul className="mt-4 space-y-2">
        {spots.map((spot) => (
          <li key={spot.id}>
            <Link
              to={`/spot/${spot.slug}`}
              className="group flex items-center gap-3 rounded-md border border-background-200 bg-background-100/60 p-2 transition-colors duration-200 hover:border-accent-300 hover:bg-accent-100/70"
            >
              <span className="h-12 w-14 shrink-0 overflow-hidden rounded-md">
                <SafeImage
                  src={spot.image}
                  fallback={getSpotFallbackImage(spot)}
                  alt={`${spot.name} - ${spot.category}`}
                  title={`${spot.name} in ${districtName}`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-foreground-900 transition-colors group-hover:text-accent-800">
                  {spot.name}
                </span>
                <span className="mt-0.5 block text-xs text-foreground-500">{spot.category}</span>
              </span>
              <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400 transition-colors group-hover:text-accent-700">
                <i className="ri-arrow-right-s-line text-base" />
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <Link
        to={`/location/${districtSlug}`}
        className="mt-4 inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
      >
        Explore {districtName}
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-right-line text-base" />
        </span>
      </Link>
    </div>
  );
}